<!-- src/components/layout/ClientLayout.vue -->
<template>
  <div class="min-h-screen bg-gray-100">
    <!-- Sidebar mobile -->
    <transition name="slide">
      <div v-if="sidebarOpen" 
           class="fixed inset-0 flex z-40 md:hidden" 
           role="dialog" 
           aria-modal="true">
        <div class="fixed inset-0 bg-gray-600 bg-opacity-75" 
             aria-hidden="true" 
             @click="sidebarOpen = false"></div>
        
        <div class="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-primary-600">
          <div class="absolute top-0 right-0 -mr-12 pt-2">
            <button type="button" 
                    class="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    @click="sidebarOpen = false">
              <span class="sr-only">{{ $t('commun.fermer') }}</span>
              <svg class="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <!-- Marque UltraLINK, comme le bandeau de l'app (05/10/2026) ; le logo de la société
               reste sur la page de connexion. -->
          <div class="flex-shrink-0 px-4 py-3">
            <marque-ultra-link fond="marine" />
          </div>
          
          <div class="mt-5 flex-1 h-0 overflow-y-auto">
            <nav class="px-2 space-y-1">
              <router-link to="/client" 
                           class="sidebar-link" 
                           :class="{ active: currentRoute.path === '/client' }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                </svg>
                {{ $t('menu.tableauDeBord') }}
              </router-link>

              <!-- Rattacher un appareil : c'est le premier geste d'un nouveau client, et il
                   n'était accessible que depuis un bouton du tableau de bord. Depuis la fiche
                   d'un appareil, il n'y avait plus aucun chemin pour en ajouter un second. -->
              <router-link v-if="estTitulaire" to="/client/claim"
                           class="sidebar-link"
                           :class="{ active: currentRoute.path === '/client/claim' }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm12 2h2v2h-2v-2zm-2 4h6" />
                </svg>
                {{ $t('menu.ajouterAppareil') }}
              </router-link>
              
              <router-link v-if="estTitulaire" to="/client/membres"
                           class="sidebar-link"
                           :class="{ active: currentRoute.path.startsWith('/client/membres') }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {{ $t('menu.membres') }}
              </router-link>

              <router-link to="/client/events"
                           class="sidebar-link"
                           :class="{ active: currentRoute.path.startsWith('/client/events') }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {{ $t('menu.evenements') }}
              </router-link>

              <router-link to="/securite"
                           class="sidebar-link"
                           :class="{ active: currentRoute.path.startsWith('/securite') }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                {{ $t('menu.securite') }}
              </router-link>

              <!-- Ajout du lien vers le changement de mot de passe -->
              <router-link to="/change-password" 
                           class="sidebar-link">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                {{ $t('menu.motDePasse') }}
              </router-link>
              
              <a @click="logout" class="sidebar-link cursor-pointer">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {{ $t('menu.deconnexion') }}
              </a>
            </nav>
          </div>
        </div>
      </div>
    </transition>
    
    <!-- Sidebar desktop -->
    <div class="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
      <div class="flex-1 flex flex-col min-h-0 bg-primary-600">
        <div class="flex flex-shrink-0 items-center px-4 py-4 border-b border-primary-500/40">
          <marque-ultra-link fond="marine" />
        </div>
        
        <div class="flex-1 flex flex-col overflow-y-auto">
          <nav class="flex-1 px-2 py-4 space-y-1">
            <router-link to="/client" 
                         class="sidebar-link" 
                         :class="{ active: currentRoute.path === '/client' }">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              {{ $t('menu.tableauDeBord') }}
            </router-link>

            <!-- Rattacher un appareil : c'est le premier geste d'un nouveau client, et il
                 n'était accessible que depuis un bouton du tableau de bord. Depuis la fiche
                 d'un appareil, il n'y avait plus aucun chemin pour en ajouter un second. -->
            <router-link v-if="estTitulaire" to="/client/claim"
                         class="sidebar-link"
                         :class="{ active: currentRoute.path === '/client/claim' }">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm12 2h2v2h-2v-2zm-2 4h6" />
              </svg>
              {{ $t('menu.ajouterAppareil') }}
            </router-link>
            
            <router-link v-if="estTitulaire" to="/client/membres"
                           class="sidebar-link"
                           :class="{ active: currentRoute.path.startsWith('/client/membres') }">
                <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {{ $t('menu.membres') }}
              </router-link>

              <router-link to="/client/events"
                         class="sidebar-link"
                         :class="{ active: currentRoute.path.startsWith('/client/events') }">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              {{ $t('menu.evenements') }}
            </router-link>

            <router-link to="/securite"
                         class="sidebar-link"
                         :class="{ active: currentRoute.path.startsWith('/securite') }">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              {{ $t('menu.securite') }}
            </router-link>

            <!-- Ajout du lien vers le changement de mot de passe -->
            <router-link to="/change-password" 
                         class="sidebar-link">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              {{ $t('menu.motDePasse') }}
            </router-link>
            
            <a @click="logout" class="sidebar-link cursor-pointer">
              <svg class="mr-3 h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {{ $t('menu.deconnexion') }}
            </a>
          </nav>
        </div>
      </div>
    </div>
    
    <!-- Main content -->
    <div class="md:pl-64 flex flex-col flex-1">
      <div class="sticky top-0 z-10 flex-shrink-0 flex h-16 bg-white shadow">
        <button type="button" 
                class="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
                @click="sidebarOpen = true">
          <span class="sr-only">{{ $t('menu.ouvrir') }}</span>
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        
        <div class="flex-1 px-4 flex justify-between">
          <div class="flex-1 flex items-center">
            <h1 class="text-2xl font-semibold text-gray-900">{{ title || $t('menu.tableauDeBord') }}</h1>
          </div>
          
          <div class="ml-4 flex items-center gap-4 md:ml-6">
            <!-- Choix de la langue : l'espace client est proposé en 5 langues (l'admin reste en FR). -->
            <langue-select />
            <div class="flex items-center">
              <span class="text-sm font-medium text-gray-700 mr-2">{{ user.name }}</span>
              <span class="h-8 w-8 rounded-full bg-primary-600 flex items-center justify-center text-white">
                {{ userInitials }}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      <main class="flex-1 pb-8">
        <div class="py-6 px-4 sm:px-6 lg:px-8">
          <slot></slot>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue';
import MarqueUltraLink from '@/components/common/MarqueUltraLink.vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import LangueSelect from '@/components/common/LangueSelect.vue';

export default defineComponent({
  name: 'ClientLayout',
  components: { LangueSelect, MarqueUltraLink },
  
  props: {
    title: {
      type: String,
      default: ''
    }
  },
  
  setup() {
    const store = useStore();
    const router = useRouter();
    const currentRoute = useRoute();
    
    const sidebarOpen = ref(false);
    
    const user = computed(() => store.getters['auth/user']);
    // Comptes partagés : seul le titulaire voit « Ajouter un appareil » et « Membres du compte ».
    // Une session ouverte avant la mise en service n'a pas de `memberRole` : c'est un titulaire.
    const estTitulaire = computed(() => !user.value || !user.value.memberRole || user.value.memberRole === 'titulaire');
    
    const userInitials = computed(() => {
      if (!user.value || !user.value.name) return '';
      
      const nameParts = user.value.name.split(' ');
      if (nameParts.length > 1) {
        return (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase();
      }
      
      return user.value.name.charAt(0).toUpperCase();
    });
    
    const logout = async () => {
      try {
        // Déconnexion avec attente de la fin de l'action
        await store.dispatch('auth/logout');
        
        // Redirection forcée avec reload pour s'assurer que tout est rechargé
        window.location.href = '/login';
      } catch (error) {
        console.error('Erreur lors de la déconnexion:', error);
        // Redirection de secours en cas d'erreur
        window.location.href = '/login';
      }
    };
    
    return {
      sidebarOpen,
      user,
      userInitials,
      estTitulaire,
      currentRoute,
      logout
    };
  }
});
</script>

<style lang="scss" scoped>
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease-out;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
